/**
 * Earth Therapy — product page behaviour.
 *
 * Requested by sections/main-product.liquid. Holds the variant picker,
 * gallery, quantity stepper and sticky buy bar, none of which exist on any
 * other template.
 */
(function () {
  'use strict';

  var core = window.Theme && window.Theme.core;
  if (!core) return;

  var define = core.define;
  var strings = core.strings;
  var formatMoney = core.formatMoney;

  /* ------------------------------------------------------- <et-quantity> */

  class EtQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.addEventListener('click', function (event) {
        var step = event.target.closest('[data-qty-step]');
        if (!step) return;

        event.preventDefault();
        var min = parseInt(this.input.min, 10) || 0;
        var max = parseInt(this.input.max, 10) || Infinity;
        var next =
          (parseInt(this.input.value, 10) || min) +
          parseInt(step.getAttribute('data-qty-step'), 10);

        this.input.value = Math.min(max, Math.max(min, next));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }.bind(this));
    }
  }

  define('et-quantity', EtQuantity);

  /* -------------------------------------------------- <et-variant-picker> */

  /**
   * Keeps the hidden variant id, price, availability and URL in sync with the
   * selected options. Without this the customer can add the wrong variant.
   */
  class EtVariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('data-section');
      this.productUrl = this.getAttribute('data-url');
      this.variants = this.parseVariants();
      if (!this.variants) return;

      this.addEventListener('change', this.onChange.bind(this));
    }

    parseVariants() {
      var script = this.querySelector('[type="application/json"]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
    }

    get selectedOptions() {
      return Array.prototype.map.call(
        this.querySelectorAll('select[data-option-index]'),
        function (select) {
          return select.value;
        }
      ).concat(
        Array.prototype.map.call(
          this.querySelectorAll('input[type="radio"]:checked'),
          function (input) {
            return input.value;
          }
        )
      );
    }

    onChange() {
      var chosen = this.selectedOptions;

      var variant = this.variants.find(function (candidate) {
        return candidate.options.every(function (option, index) {
          return option === chosen[index];
        });
      });

      this.updateInput(variant);
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateUrl(variant);
      this.updateMedia(variant);
    }

    updateInput(variant) {
      var input = document.querySelector('[data-variant-id]');
      if (input) input.value = variant ? variant.id : '';
    }

    updatePrice(variant) {
      var el = document.querySelector('[data-variant-price]');
      if (!el || !variant) return;

      var current = el.querySelector('.et-price__current');
      if (current) current.textContent = formatMoney(variant.price);

      var compare = el.querySelector('.et-price__compare');
      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

      el.classList.toggle('et-price--on-sale', Boolean(onSale));
      if (compare) {
        compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        compare.hidden = !onSale;
      }
    }

    updateButton(variant) {
      var button = document.querySelector('[data-add-button]');
      if (!button) return;

      var label = button.querySelector('.et-btn__label');
      var unavailable = !variant || !variant.available;

      button.disabled = unavailable;
      button.setAttribute('aria-disabled', unavailable ? 'true' : 'false');

      if (label) {
        label.textContent = !variant
          ? strings.unavailable || strings.soldOut
          : variant.available
          ? strings.addToCart
          : strings.soldOut;
      }
    }

    updateUrl(variant) {
      if (!variant || !this.productUrl) return;
      window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
    }

    updateMedia(variant) {
      if (!variant || !variant.featured_media_id) return;
      var gallery = document.querySelector('[data-product-gallery]');
      if (!gallery) return;

      var target = gallery.querySelector(
        '[data-media-id="' + variant.featured_media_id + '"]'
      );
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ block: 'nearest', inline: 'center' });
        target.click();
      }
    }
  }

  define('et-variant-picker', EtVariantPicker);

  /* ------------------------------------------------- <et-product-gallery> */

  /** Thumbnail to main-image switching, keyboard operable. */
  /**
   * Keeps the swipeable track, the thumbnails and the position counter in
   * step. The track scrolls natively, so swiping works with or without this;
   * the element only adds thumbnail navigation and the indicators.
   */
  class EtProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.track = this.querySelector('[data-gallery-track]');
      this.strip = this.querySelector('[data-gallery-thumbs]');
      this.current = this.querySelector('[data-gallery-current]');
      this.slides = this.track
        ? Array.prototype.slice.call(this.track.querySelectorAll('[data-gallery-slide]'))
        : [];
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-media-id]'));
      if (this.slides.length < 2) return;
      this.ready = true;
      this.index = 0;
      this.frame = null;

      this.addEventListener('click', function (event) {
        var thumb = event.target.closest('[data-media-id]');
        if (!thumb) return;
        event.preventDefault();
        this.select(thumb);
      }.bind(this));

      this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });

      if (this.strip) {
        var update = this.updateStrip.bind(this);
        this.strip.addEventListener('scroll', update, { passive: true });
        if (window.ResizeObserver) new window.ResizeObserver(update).observe(this.strip);
        update();
      }
    }

    /** Also the entry point for the variant picker, via thumbnail click(). */
    select(thumb) {
      var id = thumb.getAttribute('data-media-id');
      for (var i = 0; i < this.slides.length; i++) {
        if (this.slides[i].getAttribute('data-gallery-slide') === id) {
          this.goTo(i);
          return;
        }
      }
    }

    goTo(i) {
      var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({
        left: i * this.track.clientWidth * this.direction(),
        behavior: still ? 'auto' : 'smooth'
      });
      this.setActive(i);
    }

    direction() {
      return window.getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    }

    onScroll() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(
        function () {
          this.frame = null;
          var width = this.track.clientWidth;
          if (!width) return;
          var i = Math.round(Math.abs(this.track.scrollLeft) / width);
          if (i !== this.index) this.setActive(i);
        }.bind(this)
      );
    }

    setActive(i) {
      i = Math.max(0, Math.min(this.slides.length - 1, i));
      this.index = i;
      if (this.current) this.current.textContent = String(i + 1);

      var id = this.slides[i].getAttribute('data-gallery-slide');
      var active = null;
      this.thumbs.forEach(function (el) {
        var on = el.getAttribute('data-media-id') === id;
        el.setAttribute('aria-current', on ? 'true' : 'false');
        if (on) active = el;
      });

      // Keep the active thumbnail in view by scrolling the strip itself —
      // scrollIntoView would also move the page.
      if (active && this.strip) {
        var a = active.getBoundingClientRect();
        var s = this.strip.getBoundingClientRect();
        if (a.left < s.left || a.right > s.right) {
          this.strip.scrollBy({ left: a.left + a.width / 2 - (s.left + s.width / 2) });
        }
      }
    }

    /** Marks which edges of the thumbnail strip have more behind them. */
    updateStrip() {
      var strip = this.strip;
      var max = strip.scrollWidth - strip.clientWidth;
      var at = Math.abs(strip.scrollLeft);
      var start = at > 1;
      var end = max - at > 1;
      var more = start && end ? 'both' : start ? 'start' : end ? 'end' : '';
      if (more) strip.setAttribute('data-more', more);
      else strip.removeAttribute('data-more');
    }
  }

  define('et-product-gallery', EtProductGallery);

  /* --------------------------------------------------------- <et-sticky-atc> */

  /**
   * Mobile add-to-cart bar. Appears once the real buy button has scrolled out
   * of view, so the primary control is never duplicated on screen.
   *
   * It submits the same product form rather than carrying its own, so price,
   * variant and quantity can never drift out of sync with the main form.
   */
  class EtStickyAtc extends HTMLElement {
    connectedCallback() {
      this.anchor = document.querySelector(this.getAttribute('data-watch') || '[data-add-button]');
      if (!this.anchor || !('IntersectionObserver' in window)) return;

      this.hidden = true;

      this.observer = new IntersectionObserver(
        function (entries) {
          // Show only when the real button is above the viewport, not when the
          // customer has simply not scrolled down to it yet.
          var entry = entries[0];
          var scrolledPast = entry.boundingClientRect.top < 0;
          this.hidden = entry.isIntersecting || !scrolledPast;
        }.bind(this),
        { threshold: 0 }
      );

      this.observer.observe(this.anchor);

      var button = this.querySelector('[data-sticky-submit]');
      if (button) {
        button.addEventListener(
          'click',
          function () {
            if (this.anchor.disabled) return;
            this.anchor.click();
          }.bind(this)
        );
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  define('et-sticky-atc', EtStickyAtc);
})();
